import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

export default function AdminSMTP() {
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    use_tls: true,
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/smtp`);
      if (response.data.configured) {
        setConfig({
          smtp_host: response.data.smtp_host || '',
          smtp_port: response.data.smtp_port || 587,
          smtp_username: response.data.smtp_username || '',
          smtp_password: response.data.smtp_password || '',
          from_email: response.data.from_email || '',
          use_tls: response.data.use_tls !== false,
        });
        setIsConfigured(true);
      }
    } catch (error) {
      toast.error('Failed to fetch SMTP config');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/admin/smtp`, config);
      toast.success('SMTP configuration saved!');
      setIsConfigured(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!isConfigured) {
      toast.error('Please save configuration before testing');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(`${API}/admin/smtp/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="retro-panel text-center py-12">
        <div className="text-2xl glow-text" style={{ color: '#FF6B00' }}>
          [LOADING SMTP CONFIG...]
        </div>
      </div>
    );
  }

  return (
    <div className="retro-panel">
      <h2 className="text-3xl font-bold mb-6 glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
        [EMAIL SMTP CONFIGURATION]
      </h2>

      {isConfigured && (
        <div className="mb-4 p-3 border-2" style={{ borderColor: '#00FF00', background: 'rgba(0, 255, 0, 0.1)' }}>
          <div className="text-lg" style={{ color: '#00FF00' }}>
            ✓ SMTP Configured
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              SMTP Host:
            </label>
            <input
              type="text"
              name="smtp_host"
              data-testid="smtp-host-input"
              value={config.smtp_host}
              onChange={handleChange}
              className="retro-input w-full"
              placeholder="smtp.gmail.com"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              SMTP Port:
            </label>
            <input
              type="number"
              name="smtp_port"
              data-testid="smtp-port-input"
              value={config.smtp_port}
              onChange={handleChange}
              className="retro-input w-full"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              SMTP Username:
            </label>
            <input
              type="text"
              name="smtp_username"
              data-testid="smtp-username-input"
              value={config.smtp_username}
              onChange={handleChange}
              className="retro-input w-full"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              SMTP Password:
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="smtp_password"
                data-testid="smtp-password-input"
                value={config.smtp_password}
                onChange={handleChange}
                className="retro-input w-full"
                placeholder={isConfigured ? '********' : 'password'}
                required={!isConfigured}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm"
                style={{ color: '#FF6B00' }}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              From Email:
            </label>
            <input
              type="email"
              name="from_email"
              data-testid="smtp-from-email-input"
              value={config.from_email}
              onChange={handleChange}
              className="retro-input w-full"
              placeholder="noreply@yourdomain.com"
              required
            />
          </div>

          <div className="flex items-center gap-3 pt-8">
            <input
              type="checkbox"
              name="use_tls"
              data-testid="smtp-use-tls-checkbox"
              checked={config.use_tls}
              onChange={handleChange}
              className="w-5 h-5"
              style={{ accentColor: '#FF6B00' }}
            />
            <label className="text-lg font-semibold" style={{ color: '#ffffff' }}>
              Use TLS (Recommended)
            </label>
          </div>
        </div>

        <div className="pt-4 border-t-2" style={{ borderColor: 'rgba(255, 107, 0, 0.3)' }}>
          <div className="text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            <strong style={{ color: '#FF6B00' }}>Common SMTP Settings:</strong>
            <br />
            • Gmail: smtp.gmail.com, Port 587 (Use App Password, not regular password)
            <br />
            • Outlook: smtp-mail.outlook.com, Port 587
            <br />
            • SendGrid: smtp.sendgrid.net, Port 587
            <br />• AWS SES: email-smtp.[region].amazonaws.com, Port 587
          </div>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            data-testid="smtp-save-button"
            className="retro-btn retro-btn-green flex-1"
            disabled={saving}
          >
            {saving ? '[SAVING...]' : '[SAVE CONFIG]'}
          </button>
          <button
            type="button"
            onClick={handleTest}
            data-testid="smtp-test-button"
            className="retro-btn retro-btn-yellow flex-1"
            disabled={testing || !isConfigured}
          >
            {testing ? '[TESTING...]' : '[TEST EMAIL]'}
          </button>
        </div>
      </form>
    </div>
  );
}
